import React, { useContext } from "react";
import { useFormik } from "formik";
import Checkbox from "~/assets/js/components/Forms/Checkbox";
import TextInput from "~/assets/js/components/Forms/TextInput";
import i18next from "i18next";
import StickyFooter from "~/assets/js/components/StickyFooter";
import { AddToast } from "~/assets/js/components/Toast";
import CheckoutSummary from "~/assets/js/components/CheckoutSummary";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import Headline from "~/assets/js/components/Typography/Headline";
import useAreaCookie from "~/assets/js/Helpers/hooks/useAreaCookie";
import { CheckoutContext, ACTIONS, PAGES } from "../../CheckoutProvider";
import AddressInfoFields from "./AddressInfoFields";
import { validate } from "./ContactFormValidation";
import isEmpty from "lodash.isempty";
import { DesktopGrid } from "~/assets/js/components/Layout/DesktopGrid";
import Radio from "~/assets/js/components/Forms/Radio";
import { AlertTriangle } from "react-feather";
import { useCartContext } from "~/assets/js/global/cartContext";

const getInitialContactData = (user, address) => {
    return {
        company: user?.company || "",
        phone: user?.phone || address?.contact?.phone || "",
        email: user?.email || address?.contact?.email || "",
    };
};

const setInitialAddressData = ({ addressInfo, user }) => {
    let address = {
        first_name: addressInfo.first_name,
        last_name: addressInfo.last_name,
        company: addressInfo.company,
        legal_form: addressInfo.legal_form,
        address: addressInfo.address,
        postcode: addressInfo.postcode,
        city: addressInfo.city,
        country: addressInfo.country || "DE", // READ-ONLY FIELD
    };

    // Prefill if user logged in && cartAPI has no information
    if (
        !isEmpty(user) &&
        (address.first_name === "" || address.last_name === "")
    ) {
        address.first_name = user.first_name;
        address.last_name = user.last_name;
    }

    return address;
};

const transformCompany = (address) =>
    address.legal_form &&
    !address.company
        ?.toLowerCase()
        .includes(" " + address.legal_form.toLowerCase())
        ? `${address.company} ${address.legal_form}`
        : address.company;

const ContactDetails = () => {
    const cookie = useAreaCookie();
    const { cart, cartLoading, requiresPayment, actions } = useCartContext();
    const isDelivery = cookie.area.type === "delivery";
    const { state, dispatch } = useContext(CheckoutContext);
    const { user, orderAddress, isCompanyOrder } = state;
    const { isDesktop } = useViewport();

    const NEXT_PAGE = requiresPayment
        ? PAGES.PAYMENT_SELECT
        : PAGES.TIME_SLOT_SELECT;

    const userLoggedIn = () => {
        return !!user && !isEmpty(user);
    };

    const handleManualFormikUpdate = (field, value) =>
        formik.setFieldValue(field, value);

    const formik = useFormik({
        initialValues: {
            deliveryAddress: setInitialAddressData({
                addressInfo: orderAddress.shipping_address,
                user,
            }),
            billingAddress: setInitialAddressData({
                addressInfo: orderAddress.billing_address,
                user,
            }),
            sameAsBilling: orderAddress.sameAsBilling,
            is_company: isCompanyOrder,
            ...getInitialContactData(user, orderAddress),
        },

        onSubmit: async (values) => {
            // If pickup or sameAsBilling save same address for both
            if (!isDelivery || values.sameAsBilling) {
                values.billingAddress = values.deliveryAddress;
            }

            const apiData = { ...values };
            apiData.billingAddress = {
                ...apiData.billingAddress,
                company: transformCompany(apiData.billingAddress),
            };
            apiData.deliveryAddress = {
                ...apiData.deliveryAddress,
                company: transformCompany(apiData.deliveryAddress),
            };

            // HANDLE API ACTION__________
            const actionResponse = await actions.updateAddressData(
                apiData,
                userLoggedIn()
            );

            // HANDLE RESPONSE__________

            if (!actionResponse.ok) {
                return dispatch(
                    AddToast(
                        "danger",
                        i18next.t("toasts.userDetails.error"),
                        () => formik.submitForm()
                    )
                );
            }

            const orderType = values.is_company ? "B2B" : "B2C";

            const dataToBeSavedLocally = {
                ...user,
                sameAsBilling: values.sameAsBilling,
                billing_address: {
                    ...values.billingAddress,
                    legal_form: values.billingAddress.legal_form,
                    customer_type: orderType,
                },
                shipping_address: {
                    ...values.deliveryAddress,
                    legal_form: values.deliveryAddress.legal_form,
                    customer_type: orderType,
                },
                contact: {
                    phone: values.phone,
                    email: values.email,
                },
            };

            dispatch({
                type: ACTIONS.IS_ORDER_FOR_COMPANY,
                payload: values.is_company,
            });

            dispatch({
                type: ACTIONS.SAVE_ORDER_ADDRESS,
                payload: dataToBeSavedLocally,
            });

            dispatch({
                type: ACTIONS.CHANGE_PAGE,
                payload: NEXT_PAGE,
            });
        },
        validate,
    });

    const showBilling = !formik.values.sameAsBilling && isDelivery;

    return (
        <>
            <DesktopGrid
                leftSide={
                    <>
                        <form onSubmit={formik.handleSubmit} id="contact-form">
                            {isDelivery ? (
                                <>
                                    <Headline size={5} resizable>
                                        {i18next.t("userDetails.title")}
                                    </Headline>
                                    <p>{i18next.t("userDetails.subtitle")}</p>
                                </>
                            ) : (
                                <>
                                    <Headline size={5} resizable>
                                        {i18next.t("userDetails.pickup.title")}
                                    </Headline>
                                    <p>
                                        {i18next.t(
                                            "userDetails.pickup.subtitle"
                                        )}
                                    </p>
                                </>
                            )}

                            <Headline size={6} resizable>
                                {i18next.t("form.label.companyOrPerson")}
                            </Headline>
                            <div className="is-company-radio-container">
                                <Radio
                                    customStyles="is-company__option"
                                    bindName="is_company"
                                    onValueChange={() =>
                                        handleManualFormikUpdate(
                                            "is_company",
                                            false
                                        )
                                    }
                                    value={false}
                                    currentValue={formik.values.is_company}
                                >
                                    <p>{i18next.t("form.label.person")}</p>
                                </Radio>
                                <Radio
                                    customStyles="is-company__option"
                                    bindName="is_company"
                                    onValueChange={() =>
                                        handleManualFormikUpdate(
                                            "is_company",
                                            true
                                        )
                                    }
                                    value={true}
                                    currentValue={formik.values.is_company}
                                >
                                    <p>{i18next.t("form.label.company")}</p>
                                </Radio>
                            </div>
                            {formik.touched.is_company &&
                                !!formik.errors.is_company && (
                                    <span className="pottsalat-form__error-msg">
                                        <AlertTriangle />
                                        {i18next.t(formik.errors.is_company)}
                                    </span>
                                )}

                            <AddressInfoFields
                                prefix="deliveryAddress"
                                addressFields={formik.values.deliveryAddress}
                                addressErrors={formik.errors.deliveryAddress}
                                addressTouched={formik.touched.deliveryAddress}
                                formik={formik}
                            />

                            {isDelivery && (
                                <>
                                    <Checkbox
                                        bindName="sameAsBilling"
                                        value={formik.values.sameAsBilling}
                                        onChange={formik.handleChange}
                                        label={i18next.t(
                                            "userDetails.sameAsBilling"
                                        )}
                                    />
                                </>
                            )}
                            {showBilling && (
                                <>
                                    <Headline size={5} resizable>
                                        {i18next.t(
                                            "userDetails.billingAddress"
                                        )}
                                    </Headline>
                                    <AddressInfoFields
                                        prefix="billingAddress"
                                        addressFields={
                                            formik.values.billingAddress
                                        }
                                        addressErrors={
                                            formik.errors.billingAddress
                                        }
                                        addressTouched={
                                            formik.touched.billingAddress
                                        }
                                        formik={formik}
                                    />
                                </>
                            )}
                            <Headline size={6} resizable>
                                {i18next.t("userDetails.contact.title")}
                            </Headline>
                            <p>{i18next.t("userDetails.contact.subtitle")}</p>
                            <TextInput
                                bindName="phone"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.phone}
                                label={i18next.t("form.label.phone")}
                                error={
                                    !!formik.errors.phone &&
                                    formik.touched.phone
                                }
                                errorMessage={i18next.t(formik.errors.phone)}
                            />
                            <TextInput
                                bindName="email"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.email}
                                label={i18next.t("form.label.email")}
                                error={
                                    !!formik.errors.email &&
                                    formik.touched.email
                                }
                                errorMessage={i18next.t(formik.errors.email)}
                            />
                        </form>
                    </>
                }
                rightSide={
                    isDesktop && (
                        <CheckoutSummary
                            handleDesktopMoveForward={formik.submitForm}
                            loadingForwardButton={cartLoading}
                        />
                    )
                }
            />

            <StickyFooter
                onClick={formik.submitForm}
                totals={cart.value_gross}
                loading={cartLoading}
            />
        </>
    );
};

export default ContactDetails;
